import axios from 'axios';
import { API_CONFIG } from '../config/api';

export interface FrontendDocument {
  id: number;
  fileName: string;
  type: 'offer' | 'invoice' | 'customInvoice';
  status: 'pending' | 'confirmed' | 'declined' | 'cancelled';
  owner_id: number;
  customer_id?: number;
  advert_id?: number;
  booking_id?: number;
  calculation: any;
  positions: any[];
  additionalPositions: any[];
  taxes: any[];
  discount: number;
  discountValue: number;
  depositAmount: string;
  totalAmount: number;
  paymentComplete: boolean;
  madeDeposit: boolean;
  addressFieldsCustomer: string;
  emailFieldCustomer: string;
  bookingPersonal: any;
  bookingTiming: any;
  cancellationReason?: string;
  hidden: boolean;
  modifyDate?: string;
  created_at: string;
}

export interface CreateFrontendDocumentData {
  type: 'offer' | 'invoice' | 'customInvoice';
  status?: 'pending' | 'confirmed' | 'declined' | 'cancelled';
  fileName?: string;
  customer_id?: number;
  advert_id?: number;
  booking_id?: number;
  calculation?: any;
  positions?: any[];
  additionalPositions?: any[];
  taxes?: any[];
  discount?: number;
  discountValue?: number;
  depositAmount?: string;
  addressFieldsCustomer?: string;
  emailFieldCustomer?: string;
  bookingPersonal?: any;
  bookingTiming?: any;
}

export interface UpdateFrontendDocumentData extends Partial<CreateFrontendDocumentData> {
  madeDeposit?: boolean;
  paymentComplete?: boolean;
  cancellationReason?: string;
  hidden?: boolean;
}

/**
 * Service für die neue Frontend Document API (buchungsmanager_frontend Extension)
 */
export class FrontendDocumentService {
  
  /**
   * Erstelle ein neues Frontend Document
   */
  static async createDocument(data: CreateFrontendDocumentData): Promise<FrontendDocument> {
    try {
      const url = API_CONFIG.documents.create();
      
      console.log('🔄 Creating frontend document:', { url, data });
      
      const response = await axios.post(url, data, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      console.log('✅ Frontend document created:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Error creating frontend document:', error);
      throw error;
    }
  }

  /**
   * Aktualisiere ein Frontend Document
   */
  static async updateDocument(id: number, data: UpdateFrontendDocumentData): Promise<FrontendDocument> {
    try {
      const url = API_CONFIG.documents.update(id.toString());
      
      console.log('🔄 Updating frontend document:', { id, url, data });
      
      const response = await axios.post(url, data, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      console.log('✅ Frontend document updated:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Error updating frontend document:', error);
      throw error;
    }
  }

  /**
   * Lade ein einzelnes Frontend Document
   */
  static async getDocument(id: number): Promise<FrontendDocument> {
    try {
      const url = API_CONFIG.documents.get(id.toString());
      
      console.log('🔄 Fetching frontend document:', { id, url });
      
      const response = await axios.get(url, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      console.log('✅ Frontend document fetched:', response.data);
      return response.data.data || response.data;
      
    } catch (error) {
      console.error('❌ Error fetching frontend document:', error);
      throw error;
    }
  }

  /**
   * Lade alle Frontend Documents
   */
  static async getDocuments(filters: any = {}): Promise<FrontendDocument[]> {
    try {
      let url = API_CONFIG.documents.list();
      
      // Filter hinzufügen
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.customer_id) params.append('customer_id', filters.customer_id.toString());
      
      if (params.toString()) {
        url += `&${params.toString()}`;
      }
      
      console.log('🔄 Fetching frontend documents:', { url, filters });
      
      const response = await axios.get(url, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      const documents = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log('✅ Frontend documents fetched:', documents.length, 'items');
      return documents;
      
    } catch (error) {
      console.error('❌ Error fetching frontend documents:', error);
      throw error;
    }
  }

  /**
   * Markiere Document als bezahlt
   */
  static async markAsPaid(id: number): Promise<FrontendDocument> {
    return this.updateDocument(id, {
      paymentComplete: true,
      status: 'confirmed'
    });
  }

  /**
   * Markiere Document als storniert
   */
  static async cancelDocument(id: number, reason: string): Promise<FrontendDocument> {
    return this.updateDocument(id, {
      status: 'cancelled',
      cancellationReason: reason
    });
  }

  /**
   * Soft-Delete: Verstecke Document
   */
  static async hideDocument(id: number): Promise<FrontendDocument> {
    return this.updateDocument(id, {
      hidden: true
    });
  }

  /**
   * Erstelle Rechnung basierend auf Angebot
   */
  static async createInvoiceFromOffer(offerId: number): Promise<FrontendDocument> {
    try {
      // Erst das Angebot laden
      const offer = await this.getDocument(offerId);
      
      // Neue Rechnung mit Angebots-Daten erstellen
      const invoiceData: CreateFrontendDocumentData = {
        type: 'invoice',
        status: 'pending',
        customer_id: offer.customer_id,
        advert_id: offer.advert_id,
        booking_id: offer.booking_id,
        calculation: offer.calculation,
        positions: offer.positions,
        additionalPositions: offer.additionalPositions,
        taxes: offer.taxes,
        discount: offer.discount,
        discountValue: offer.discountValue,
        depositAmount: offer.depositAmount,
        addressFieldsCustomer: offer.addressFieldsCustomer,
        emailFieldCustomer: offer.emailFieldCustomer,
        bookingPersonal: offer.bookingPersonal,
        bookingTiming: offer.bookingTiming,
      };

      const invoice = await this.createDocument(invoiceData);
      
      // Optional: Angebot als bestätigt markieren
      await this.updateDocument(offerId, { status: 'confirmed' });
      
      return invoice;
      
    } catch (error) {
      console.error('❌ Error creating invoice from offer:', error);
      throw error;
    }
  }

  /**
   * Erstelle eine Storno-Rechnung
   */
  static async createCancellationInvoice(originalInvoiceId: number, reason: string): Promise<FrontendDocument> {
    try {
      // Originale Rechnung laden
      const originalInvoice = await this.getDocument(originalInvoiceId);
      
      // Storno-Rechnung erstellen (negative Beträge)
      const cancellationData: CreateFrontendDocumentData = {
        type: 'invoice',
        status: 'pending',
        customer_id: originalInvoice.customer_id,
        advert_id: originalInvoice.advert_id,
        booking_id: originalInvoice.booking_id,
        // Negative Beträge für Storno
        calculation: {
          ...originalInvoice.calculation,
          total: -Math.abs(originalInvoice.calculation?.total || 0),
          subtotal: -Math.abs(originalInvoice.calculation?.subtotal || 0)
        },
        positions: originalInvoice.positions.map(pos => ({
          ...pos,
          quantity: -Math.abs(pos.quantity || 0),
          total: -Math.abs(pos.total || 0)
        })),
        additionalPositions: originalInvoice.additionalPositions.map(pos => ({
          ...pos,
          quantity: -Math.abs(pos.quantity || 0),
          total: -Math.abs(pos.total || 0)
        })),
        taxes: originalInvoice.taxes,
        discount: originalInvoice.discount,
        discountValue: -Math.abs(originalInvoice.discountValue || 0),
        addressFieldsCustomer: originalInvoice.addressFieldsCustomer,
        emailFieldCustomer: originalInvoice.emailFieldCustomer,
        bookingPersonal: originalInvoice.bookingPersonal,
        bookingTiming: originalInvoice.bookingTiming,
      };

      const cancellationInvoice = await this.createDocument(cancellationData);
      
      // Originale Rechnung als storniert markieren
      await this.updateDocument(originalInvoiceId, { 
        status: 'cancelled',
        cancellationReason: reason
      });
      
      return cancellationInvoice;
      
    } catch (error) {
      console.error('❌ Error creating cancellation invoice:', error);
      throw error;
    }
  }
}

/**
 * React Hook für Frontend Document Service
 */
export const useFrontendDocumentService = () => {
  return {
    createDocument: FrontendDocumentService.createDocument,
    updateDocument: FrontendDocumentService.updateDocument,
    getDocument: FrontendDocumentService.getDocument,
    getDocuments: FrontendDocumentService.getDocuments,
    markAsPaid: FrontendDocumentService.markAsPaid,
    cancelDocument: FrontendDocumentService.cancelDocument,
    hideDocument: FrontendDocumentService.hideDocument,
    createInvoiceFromOffer: FrontendDocumentService.createInvoiceFromOffer,
    createCancellationInvoice: FrontendDocumentService.createCancellationInvoice,
  };
}; 